import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Tooltip,
  IconButton,
  Popover,
  Button,
  SliderThumb,
  Slider,
  styled,
  useTheme,
  Radio,
  DialogTitle,
  DialogContent,
  Autocomplete,
  TextField,
  FormGroup,
  alpha,
} from "@mui/material"

import {
  ArrowDownwardRounded,
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  CloseRounded,
  ContentCutRounded,
  CropFreeRounded,
  DownloadRounded,
  ExpandMoreRounded,
  InfoOutlined,
  FilterAltOffRounded,
  FilterAltRounded,
  HelpOutlineRounded,
  LegendToggleRounded,
  MoreVertRounded
} from "@mui/icons-material"

import HKVDialog from "../../HKVDialog/7.0"
import { HKVSortableList } from "../../HKVSortableList/1.0"
import { Legend } from "../../HKVMap/14.0"

import { downloadData, sortByKey } from "../../../lib/utils"

import { useResizeDetector } from "react-resize-detector"

const StyledSliderThumb = styled(SliderThumb)({
  marginRight: 0,
  boxShadow: "unset !important",
  height: 14,
  width: 14,
  "&::after": {
    display: "none"
  }
})

const buildLayerTree = (mapConfig) => {
  const allLayers = [
    ...(mapConfig?.layers || []),
    ...(mapConfig?.timeDimensionLayers || [])
  ].filter(ly => !ly.disabled)

  const tree = []

  const findOrCreateNode = (nodes, id, name) => {
    let node = nodes.find((n) => n.id === id)
    if (!node) {
      node = { id, name, items: [] }
      nodes.push(node)
    }
    return node
  }

  const sortedLayers = sortByKey(allLayers, "zIndex", "desc")

  sortedLayers.forEach((layer) => {
    const panels = Array.isArray(layer.panel)
      ? layer.panel
      : layer.panel
        ? [layer.panel]
        : []

    if (panels.length === 0) {
      // No panels → root level
      tree.push({
        id: layer.key,
        name: layer.name
      })
    } else {
      // Insert into nested structure
      let currentLevel = tree
      panels.forEach((panelName) => {
        currentLevel = findOrCreateNode(currentLevel, panelName, panelName).items
      })
      currentLevel.push({
        id: layer.key,
        name: layer.name
      })
    }
  })

  return tree
}

const LayerControl = (props) => {
  const {
    mapConfig,
    setMapConfig,
    mapFeatureGroupRefs,
    mapRef,
    onLayerChange,
    openAccordions = [],
    panelSubtitels = {},
    panelTooltips = {}
  } = props

  const { t } = useTranslation()
  const theme = useTheme()

  const [accordions, setAccordions] = useState({})
  const [layers, setLayers] = useState([])

  const [dialogConfig, setDialogConfig] = useState()

  const [anchorLayer, setAnchorLayer] = useState()
  const [anchorPosition, setAnchorPosition] = useState()
  const [showLegend, setShowLegend] = useState(false)

  const openDialog = (size, options) => {
    const mergedOptions = { ...options, open: true, size: size ? size : "lg" }
    setDialogConfig(mergedOptions)
  }

  const closeDialog = () => {
    setDialogConfig()
  }

  useEffect(() => {
    setAccordions(prevState => {
      const newState = { ...prevState }

      openAccordions.forEach(openAccordion => {
        newState[openAccordion] = true
      })

      return newState
    })
  }, [JSON.stringify(openAccordions)])

  useEffect(() => {
    const tree = buildLayerTree(mapConfig)

    const allLayers = [
      ...(mapConfig?.layers || []),
      ...(mapConfig?.timeDimensionLayers || [])
    ]

    allLayers.forEach(layer => {
      const opacity = layer.options
        ? (layer.options.opacity === 0 ? 0 : (layer.options.opacity || 1))
        : 1

      const pane = mapRef?.getPane(`${layer.key}Pane`)
      if (pane) {
        pane.style.opacity = opacity
      }
    })

    setLayers(tree)
  }, [mapConfig?.layers?.length, mapConfig?.timeDimensionLayers?.length, mapRef && Object.keys(mapRef.getPanes()).length])

  useEffect(() => {
    const flattenItems = (items, parentId = null) => {
      return items.flatMap(item => {
        const base = { id: item.id, name: item.name, parentId }
        if (item.items && item.items.length > 0) {
          return [base, ...flattenItems(item.items, item.id)]
        }
        return base
      })
    }

    const flattenedItems = layers.flatMap(layer =>
      flattenItems(layer.items || [], layer.id)
    )

    const allLayers = [
      ...(mapConfig?.layers || []),
      ...(mapConfig?.timeDimensionLayers || [])
    ]
    let zBase = 400

    flattenedItems.reverse().forEach((item, layerIndex) => {
      const fullLayer = allLayers.find((l) => l.key === item.id)

      if (!fullLayer) return
      const pane = mapRef?.getPane(`${fullLayer.key}Pane`)
      const clusterPolygonPane = mapRef?.getPane(`clusterPolygon-${fullLayer.key}Pane`)

      if (pane) {
        pane.style.zIndex = (zBase + layerIndex)
      }
      if (clusterPolygonPane) {
        clusterPolygonPane.style.zIndex = (zBase + layerIndex)
      }
    })
  }, [layers])

  // helper: recursively update nested items after sorting
  const updateNestedItems = (currentItems, id, newItems) => {
    return currentItems.map((item) => {
      if (item.id === id) {
        return { ...item, items: newItems }
      }
      if (item.items) {
        return { ...item, items: updateNestedItems(item.items, id, newItems) }
      }
      return item
    })
  }

  const downloadLayer = (layer) => {
    const featureGroupRef = mapFeatureGroupRefs[layer.key]

    if (featureGroupRef?.toGeoJSON) {
      const geojson = featureGroupRef.toGeoJSON()
      downloadData(JSON.stringify(geojson, null, 2), `${t(layer.name)}.geojson`)
      return
    }

    if (layer.features) {
      downloadData(JSON.stringify({ type: "FeatureCollection", features: layer.features }), `${t(layer.name)}.geojson`)
      return
    }

    if (layer.wms) {
      const wcsUrl = `${layer.url}?service=WCS&version=2.0.1&request=GetCoverage&CoverageId=${layer.layer}&format=image/geotiff`

      const link = document.createElement("a")
      const filename = "raster.tiff"

      if (link.download !== undefined) {
        link.setAttribute("href", wcsUrl)
        link.setAttribute("download", filename)
        link.style.visibility = "hidden"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(wcsUrl)
      }
    }
  }

  const getPadding = (placement) => {
    let padding = [0, 0]
    const drawerEl = document.getElementById(`drawer-${placement}`)

    if (drawerEl) {
      const offset = drawerEl.style.visibility !== "hidden"
        ? drawerEl.offsetWidth
        : 0

      padding[0] = offset
    }
    return padding
  }

  const flyToBounds = (bounds) => {
    const currentZoom = mapRef?.getZoom()

    if (mapRef?.flyToBounds) {
      mapRef.flyToBounds(bounds, {
        maxZoom: currentZoom > 14 ? currentZoom : 14,
        duration: 0.25,
        paddingTopLeft: getPadding("left"),
        paddingBottomRight: getPadding("right")
      })
    }
  }

  const getRasterLayerBounds = async (layer) => {
    const url = `${layer.url}?service=WMS&version=1.1.0&request=GetCapabilities&namespace=${layer.layer.split(":")[0]}`
    const response = await fetch(url)
    const text = await response.text()

    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(text, "text/xml")

    const layers = Array.from(xmlDoc.getElementsByTagName("Layer"))

    const foundLayer = layers.find((lyr) => {
      const nameNode = lyr.getElementsByTagName("Name")[0]
      return nameNode && nameNode.textContent === layer.layer
    })

    if (foundLayer) {
      const bboxNode = foundLayer.getElementsByTagName("LatLonBoundingBox")[0]
      const minx = bboxNode.getAttribute("minx")
      const miny = bboxNode.getAttribute("miny")
      const maxx = bboxNode.getAttribute("maxx")
      const maxy = bboxNode.getAttribute("maxy")
      return [[Number(miny), Number(minx)], [Number(maxy), Number(maxx)]]
    }

    return null
  }

  const getGeoJSONBounds = (features) => {
    if (!features || !features.length) return null

    let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity

    features.forEach((feature) => {
      const coords = feature.geometry?.coordinates
      if (!coords) return

      const flatten = (arr) => arr.flat(Infinity)
      const allCoords = flatten(coords)

      for (let i = 0; i < allCoords.length; i += 2) {
        const lng = allCoords[i]
        const lat = allCoords[i + 1]
        if (lat < minLat) minLat = lat
        if (lat > maxLat) maxLat = lat
        if (lng < minLng) minLng = lng
        if (lng > maxLng) maxLng = lng
      }
    })

    return [[minLat, minLng], [maxLat, maxLng]]
  }

  const centerLayer = async (layer) => {
    const featureGroupRef = mapFeatureGroupRefs[layer.key]

    if (featureGroupRef?.getBounds) {
      const bounds = featureGroupRef.getBounds()
      if (!bounds.isValid()) return
      flyToBounds(bounds)
      return
    }

    if (layer.features?.length > 0) {
      const bounds = getGeoJSONBounds(layer.features)
      if (!bounds) return
      flyToBounds(bounds)
    }

    if (layer.wms) {
      const bounds = await getRasterLayerBounds(layer)
      if (!bounds) return
      flyToBounds(bounds)
    }
  }

  const handleMenuOpen = (event, layerKey) => {
    setAnchorLayer({ key: layerKey, variant: "menu" })

    const rect = event.currentTarget.getBoundingClientRect()
    setAnchorPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX
    })
  }

  const handleMenuClose = () => {
    setAnchorLayer()
    setAnchorPosition()
  }

  const LayerPopover = ({ layer, open, onClose }) => {
    const pane = mapRef?.getPane(`${layer.key}Pane`)
    const initialOpacity = pane?.style?.opacity !== "" && pane?.style?.opacity != null
      ? Number(pane.style.opacity)
      : 1
    const [layerOpacity, setLayerOpacity] = useState(initialOpacity)

    const [adjustedAnchorPos, setAdjustedAnchorPos] = useState(anchorPosition)

    const onResize = (event) => {
      const rect = event.entry.contentRect
      const overflowBottom = rect.bottom - window.innerHeight

      if (overflowBottom > 0) {
        setAdjustedAnchorPos((prev) => ({
          ...prev,
          top: Math.max(8, prev.top - overflowBottom - 8),
        }))
      } else {
        setAdjustedAnchorPos(anchorPosition)
      }
    }

    const { ref } = useResizeDetector({ skipOnMount: true, onResize })

    if (!open) return

    return (
      <Popover
        anchorReference="anchorPosition"
        anchorPosition={adjustedAnchorPos}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              background: theme.palette.grey[100],
              width: 340,
              maxHeight: "calc(100vh - 16px)",
              overflowY: "auto"
            }
          }
        }}
        transitionDuration={0}
        elevation={6}
      >
        <div
          ref={ref}
          style={{
            display: "flex", flexDirection: "column", gap: theme.spacing(0.75),
            margin: theme.spacing(1),
            padding: theme.spacing(1),
            background: "white",
            borderRadius: theme.shape.borderRadius
          }}
        >
          <div style={{
            margin: theme.spacing(0.5),
            borderBottom: `2px solid ${theme.palette.grey[300]}`,
            marginBottom: theme.spacing(1), paddingBottom: theme.spacing(1),
          }}>
            <FormControlLabel
              control={<Checkbox
                checked={layer.checked}
                color="primary"
                onChange={(event, checked) => {
                  onLayerChange(layer, (layer.type === "time" ? "timeDimensionLayers" : "layers"))
                }}
                disabled={Boolean(layer.disabled)}
                sx={{ padding: 0, marginRight: theme.spacing(0.5) }}
              />}
              sx={{ margin: 0 }}
              slotProps={{
                root: {
                  sx: { width: "fit-content" }
                }
              }}
              label={<Typography sx={{ fontSize: theme.typography.pxToRem(18), fontWeight: 500, wordBreak: "break-all" }}>
                {t(layer.name)}
              </Typography>}
            />
          </div>
          <div style={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: theme.shape.borderRadius }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, margin: theme.spacing(0.5, 0, -0.5, 1) }}
            >
              {t("Opacity")}:
            </Typography>
            {layer?.layerControl?.opacity && <Slider
              value={layerOpacity}
              step={0.01}
              min={0}
              max={1}
              valueLabelDisplay="off"
              disabled={Boolean(layer.disabled)}
              slots={{
                thumb: StyledSliderThumb
              }}
              onChange={(event, value) => {
                pane.style.opacity = value
                setLayerOpacity(value)
              }}
              sx={{
                padding: theme.spacing(1, 0),
                margin: theme.spacing(0, 1, -0.25),
                width: `calc(100% - ${theme.spacing(2)})`
              }}
            />}
          </div>
          {layer.layerControl?.description && <Button
            disableElevation
            variant="text"
            color="inherit"
            onClick={() => openDialog("md", { layer: layer })}
            startIcon={<HelpOutlineRounded />}
            fullWidth
            sx={{ justifyContent: "flex-start", border: `1px solid ${theme.palette.grey[200]}` }}
          >
            {t("Information")}
          </Button>}
          {layer.layerControl?.download && <Button
            disableElevation
            variant="text"
            color="inherit"
            onClick={() => downloadLayer(layer)}
            startIcon={<DownloadRounded />}
            fullWidth
            sx={{ justifyContent: "flex-start", border: `1px solid ${theme.palette.grey[200]}` }}
          >
            {t("Download_layer")}
          </Button>}
          {layer.layerControl?.center && <Button
            disableElevation
            variant="text"
            color="inherit"
            onClick={() => centerLayer(layer)}
            startIcon={<CropFreeRounded />}
            fullWidth
            sx={{ justifyContent: "flex-start", border: `1px solid ${theme.palette.grey[200]}` }}
          >
            {t("Center_layer")}
          </Button>}
          {layer.layerControl?.legend && <Button
            disableElevation
            variant="text"
            color="inherit"
            onClick={() => setShowLegend(!showLegend)}
            startIcon={<LegendToggleRounded />}
            fullWidth
            sx={{
              background: showLegend ? theme.palette.grey[100] : "white",
              justifyContent: "flex-start", border: `1px solid ${theme.palette.grey[200]}`,
              "&:hover": {
                background: showLegend ? theme.palette.grey[200] : theme.palette.grey[100]
              }
            }}
          >
            {showLegend ? t("Hide_legend") : t("Show_legend")}
          </Button>}
          {layer.layerControl?.remove && <Button
            disableElevation
            variant="text"
            color="inherit"
            // onClick={() => props.removeLayer(layer)}
            startIcon={<CloseRounded />}
            fullWidth
            sx={{ justifyContent: "flex-start", border: `1px solid ${theme.palette.grey[200]}` }}
          >
            {t("Delete_layer")}
          </Button>}
          {layer.layerControl?.cut && <Button
            disableElevation
            variant="text"
            color="inherit"
            // onClick={() => props.cutRasters(layer)}
            startIcon={<ContentCutRounded />}
            fullWidth
            sx={{ justifyContent: "flex-start", border: `1px solid ${theme.palette.grey[200]}` }}
          >
            {t("Cut_layer")}
          </Button>}
          {showLegend ? <div
            style={{
              display: "flex", flexDirection: "column",
              borderTop: `2px solid ${theme.palette.grey[300]}`,
              marginTop: theme.spacing(1), paddingTop: theme.spacing(1)
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>{t("Legend")}:</Typography>
            <Legend
              layer={layer}
              mapConfig={mapConfig}
              setMapConfig={setMapConfig}
            />
          </div> : null}
        </div>
      </Popover>
    )
  }

  const LayerMenu = ({ layer }) => {
    const keys = ["center", "cut", "description", "download", "legend", "opacity", "remove"]
    if (!keys.some(k => layer.layerControl?.[k])) return

    return (
      <div style={{ flexShrink: 0 }}>
        <Tooltip
          title={t("Menu")}
          disableInteractive
        >
          <IconButton
            size="small"
            sx={{ margin: theme.spacing(0, 0, 0.25, 0.25) }}
            onClick={(e) => handleMenuOpen(e, layer.key)}
          >
            <MoreVertRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <LayerPopover
          layer={layer}
          open={(anchorLayer?.key === layer.key && anchorLayer?.variant === "menu") && Boolean(anchorPosition)}
          onClose={handleMenuClose}
        />
      </div>
    )
  }

  const handleFilterMenuOpen = (event, layerKey) => {
    setAnchorLayer({ key: layerKey, variant: "filter" })

    const rect = event.currentTarget.getBoundingClientRect()
    setAnchorPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX
    })
  }

  const handleFilterMenuClose = () => {
    setAnchorLayer()
    setAnchorPosition()
  }

  const LayerFilterPopover = ({ layer, open, onClose }) => {
    const { label, type, property, options, defaultValue, onChange, onBlur } = layer.propertyFilter

    const [adjustedAnchorPos, setAdjustedAnchorPos] = useState(anchorPosition)
    const [value, setValue] = useState(defaultValue)

    const onResize = (event) => {
      const rect = event.entry.contentRect
      const overflowBottom = rect.bottom - window.innerHeight

      if (overflowBottom > 0) {
        setAdjustedAnchorPos((prev) => ({
          ...prev,
          top: Math.max(8, prev.top - overflowBottom - 8),
        }))
      } else {
        setAdjustedAnchorPos(anchorPosition)
      }
    }

    const { ref } = useResizeDetector({ skipOnMount: true, onResize })

    if (!open) return

    const useCheckboxGroup = options.length <= 10

    return (
      <Popover
        anchorReference="anchorPosition"
        anchorPosition={adjustedAnchorPos}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              background: theme.palette.grey[100],
              width: 340,
              maxHeight: "calc(100vh - 16px)",
              overflowY: "auto"
            }
          }
        }}
        transitionDuration={0}
        elevation={6}
      >
        <div
          ref={ref}
          style={{
            display: "flex", flexDirection: "column", gap: theme.spacing(0.75),
            margin: theme.spacing(1),
            padding: theme.spacing(1),
            background: "white",
            borderRadius: theme.shape.borderRadius
          }}
        >
          <div style={{
            margin: theme.spacing(0.5),
            borderBottom: `2px solid ${theme.palette.grey[300]}`,
            marginBottom: theme.spacing(1), paddingBottom: theme.spacing(1),
          }}>
            <Typography sx={{ fontSize: theme.typography.pxToRem(18), fontWeight: 500, wordBreak: "break-all" }}>
              {t(layer.name)}
            </Typography>
          </div>
          {useCheckboxGroup
            ? <FormGroup>
              {options.map((option, ix) => {
                const id = option.hasOwnProperty("id") ? option.id : option
                const naam = option.hasOwnProperty("naam") ? option.naam : String(option)
                const isChecked = (value || []).map(String).includes(String(id))

                return (
                  <FormControlLabel
                    key={ix}
                    checked={isChecked}
                    onChange={(event) => {
                      const newValue = event.target.checked
                        ? [...(value || []), id]
                        : (value || []).filter(v => String(v) !== String(id))
                      setValue(newValue)
                      if (onChange) {
                        const selectedOptions = options.filter(opt =>
                          newValue.map(String).includes(String(opt.hasOwnProperty("id") ? opt.id : opt))
                        )
                        onChange(selectedOptions, mapRef, mapFeatureGroupRefs)
                      }
                    }}
                    control={<Checkbox sx={{ padding: 0, marginRight: theme.spacing(0.5) }} />}
                    label={naam}
                    labelPlacement="end"
                    sx={{ margin: 0, width: "fit-content" }}
                  />
                )
              })}
            </FormGroup>
            : <Autocomplete
              multiple
              limitTags={2}
              disableCloseOnSelect
              options={options}
              disabled={options.length === 0}
              size="small"
              value={value || []}
              onChange={(event, value) => {
                setValue(value.map(val => val.hasOwnProperty("id") ? val.id : val))
                if (onChange) {
                  onChange(value, mapRef, mapFeatureGroupRefs)
                }
              }}
              onClose={() => {
                if (onBlur) {
                  onBlur(value, mapRef, mapFeatureGroupRefs)
                }
                handleFilterMenuClose()
              }}
              isOptionEqualToValue={(option, value) => {
                if (option.hasOwnProperty("id")) {
                  return String(option.id) === String(value)
                } else {
                  return String(option) === String(value)
                }
              }}
              getOptionLabel={(option) => {
                if (option.hasOwnProperty("naam")) {
                  return String(option.naam)
                }
                return String(options.find(val => val.id === option)?.naam) || ""
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props
                return (
                  <li key={key} {...optionProps} style={{ padding: theme.spacing(0.25, 1.25) }}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankRounded fontSize="small" />}
                      checkedIcon={<CheckBoxRounded fontSize="small" />}
                      sx={{ marginRight: theme.spacing(1), padding: 0 }}
                      checked={selected}
                    />
                    {option.naam}
                  </li>
                )
              }}
              renderInput={(params) => {
                params.InputProps = {
                  ...params.InputProps,
                  sx: { background: "white" },
                }
                return <TextField {...params} label={label} />
              }}
              slotProps={{
                listbox: { sx: { maxHeight: 340 } }
              }}
              sx={{
                // minWidth: `calc(230px - ${theme.spacing(1)})`, maxWidth: "calc(320px + 160px - 90px)",
                maxWidth: "100%",
                "& .MuiAutocomplete-inputRoot": {
                  "& .MuiAutocomplete-input": {
                    minWidth: 0,
                    padding: "2.5px 0 !important",
                    flexGrow: 1
                  }
                }
              }}
            />
          }
        </div>
      </Popover>
    )
  }

  const LayerPropertyFilter = ({ layer }) => {
    return (
      <div style={{ flexShrink: 0 }}>
        <Tooltip
          title={t("Filter")}
          disableInteractive
        >
          <IconButton
            size="small"
            sx={{ position: "relative", margin: theme.spacing(0, 0, 0.25, 0.25) }}
            onClick={(e) => handleFilterMenuOpen(e, layer.key)}
          >
            <FilterAltRounded fontSize="small" />
            <ArrowDownwardRounded sx={{ position: "absolute", bottom: theme.spacing(0.25), right: theme.spacing(0.25), fontSize: theme.typography.pxToRem(12) }} />
          </IconButton>
        </Tooltip>
        <LayerFilterPopover
          layer={layer}
          open={(anchorLayer?.key === layer.key && anchorLayer?.variant === "filter") && Boolean(anchorPosition)}
          onClose={handleFilterMenuClose}
        />
      </div>
    )
  }

  const renderLayerControl = (layer, type) => {
    if (type === "baselayers") {
      layer.layerControl = { type: "radio" }
    }

    const layerControlType = layer?.layerControl?.type

    switch (layerControlType) {
      case "radio":
        return (
          <Radio
            checked={layer.checked}
            color="primary"
            size="small"
            onClick={() => {
              if (layer.checked) {
                onLayerChange(layer, (type === "baselayers" ? "baseLayers" : (layer.type === "time" ? "timeDimensionLayers" : "layers")), layerControlType)
              }
            }}
            onChange={() => {
              onLayerChange(layer, (type === "baselayers" ? "baseLayers" : (layer.type === "time" ? "timeDimensionLayers" : "layers")), layerControlType)
            }}
            sx={{ padding: 0, marginRight: theme.spacing(0.5) }} //margin: theme.spacing(0.25, 0.5, 0.25, 0.25) }}
          />
        )

      case "checkbox":
      default:
        return (
          <Checkbox
            checked={layer.checked}
            color="primary"
            size="small"
            onChange={() => {
              onLayerChange(layer, (type === "baselayers" ? "baseLayers" : (layer.type === "time" ? "timeDimensionLayers" : "layers")), layerControlType)
            }}
            sx={{ padding: 0, marginRight: theme.spacing(0.5) }} //margin: theme.spacing(0.25, 0.5, 0.25, 0.25) }}
          />
        )
    }
  }

  const countCheckedLayers = (items, allLayers) => {
    let count = 0
    for (const item of items) {
      if (Array.isArray(item.items)) {
        // recurse into nested groups
        count += countCheckedLayers(item.items, allLayers)
      } else {
        const fullLayer = allLayers.find(l => l.key === item.id)
        if (fullLayer?.checked) count++
      }
    }
    return count
  }

  // recursive renderer
  const renderSortableGroup = (items, parentId = null, depth = 0, variant) => {
    const showHandles = items.length > 1
    const isBase = variant === "baselayers"

    // Cap de diepte zodat diep geneste groepen niet blijven krimpen
    const cappedDepth = Math.min(depth, 3)

    const currentHeight = isBase
      ? "unset"
      : Math.max(48 - cappedDepth * 6, 32)

    const fontSizePx = isBase
      ? 16
      : Math.max(12.5, 14.5 - cappedDepth)

    const fontSize = theme.typography.pxToRem(fontSizePx)
    const subtitleFontSize = theme.typography.pxToRem(fontSizePx - 1)

    const railColor = alpha(theme.palette.primary.main, 0.4)
    const groupBackground = depth % 2 === 0
      ? theme.palette.background.paper
      : theme.palette.grey[50]

    return (
      <HKVSortableList
        items={items}
        onChange={(newItems) => {
          if (parentId) {
            setLayers((prev) => updateNestedItems(prev, parentId, newItems))
          } else {
            setLayers(newItems)
          }
        }}
        renderItem={(item, index) => {
          const isFirstNestedAccordion = index === 0 && variant !== "baselayers"//depth > 0 && index === 0
          const allLayers = [
            ...(mapConfig?.layers || []),
            ...(mapConfig?.timeDimensionLayers || []),
            ...(mapConfig?.baseLayers || [])
          ]

          const fullLayer = allLayers.find((l) => l.key === item.id)
          const checkedItems = countCheckedLayers([item], allLayers)

          const backgroundColor = variant === "baselayers" ? "white" : theme.palette.grey[depth * 100]

          let itemHeight = currentHeight
          if (panelSubtitels.hasOwnProperty(item.id)) {
            itemHeight += 12
          }

          return (
            <HKVSortableList.Item
              id={item.id}
              style={{
                marginTop: isFirstNestedAccordion
                  ? 0
                  : theme.spacing(0.5)
              }}
            >
              {Array.isArray(item.items) ? (
                <Accordion
                  elevation={0}
                  expanded={Boolean(accordions[item.id])}
                  onChange={(event, expanded) => {
                    setAccordions(prevState => ({
                      ...prevState,
                      [item.id]: expanded
                    }))
                  }}
                  slotProps={{
                    root: {
                      sx: {
                        background: isBase ? "white" : groupBackground,
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderLeft: depth > 0
                          ? `3px solid ${railColor}`
                          : `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: `${theme.shape.borderRadius}px`,
                        overflow: "hidden",
                        "&::before": { display: "none" } // verwijdert de default bovenlijn van MUI
                      }
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreRounded
                      sx={{ fontSize: theme.typography.pxToRem(Math.max(18, 24 - cappedDepth * 2)) }}
                    />}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: showHandles
                        ? theme.spacing(0, 1.5, 0, 0.5)
                        : theme.spacing(0, 1.5, 0, 1),
                      minHeight: `${itemHeight}px !important`,
                      height: "fit-content",

                      "& .MuiAccordionSummary-content": {
                        minWidth: 0,
                        margin: theme.spacing(cappedDepth > 1 ? 0.75 : 1, 0)
                      },
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        alignSelf: "flex-start",
                        marginTop: theme.spacing(cappedDepth > 1 ? 0.75 : 1)
                      }
                    }}
                  >
                    {(showHandles && variant !== "baselayers" && !fullLayer?.disabled && !fullLayer?.inactive) && <Tooltip title={t("Drag_layer_order")}>
                      <div><HKVSortableList.DragHandle fontSize={depth > 1 ? "small" : "medium"} /></div>
                    </Tooltip>}
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", gap: theme.spacing(0.5) }}>
                        <Typography sx={{ fontSize: fontSize, fontWeight: 500 }}>
                          {(checkedItems && variant !== "baselayers") > 0
                            ? `${t(fullLayer?.name || item.name)} (${checkedItems})`
                            : t(fullLayer?.name || item.name)
                          }
                        </Typography>
                        {panelTooltips[item.id] && (
                          <Tooltip title={t(panelTooltips[item.id])} arrow placement="right">
                            <InfoOutlined
                              sx={{ opacity: 0.5, flexShrink: 0, fontSize: theme.typography.pxToRem(17) }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Tooltip>
                        )}
                      </div>
                      <Typography sx={{ fontSize: subtitleFontSize, fontWeight: 400, lineHeight: 1 }}>
                        {t(panelSubtitels[item.id])}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: isBase
                        ? theme.spacing(0, 1, 1)
                        : theme.spacing(0, 0.5, 0.5, depth > 0 ? 1 : 0.5)
                    }}
                  >
                    {renderSortableGroup(item.items, item.id, depth + 1, variant)}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Box
                  sx={{
                    background: theme.palette.background.paper,
                    padding: isBase ? "unset" : theme.spacing(0.5),
                    border: isBase ? "unset" : `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: `${theme.shape.borderRadius}px`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {(showHandles && variant !== "baselayers" && !fullLayer?.disabled && !fullLayer?.inactive) && <Tooltip title={t("Drag_layer_order")}>
                        <div><HKVSortableList.DragHandle fontSize={depth > 1 ? "small" : "medium"} /></div>
                      </Tooltip>}
                      {fullLayer && <FormControlLabel
                        control={renderLayerControl(fullLayer, variant)}
                        sx={{ margin: 0 }}
                        disabled={Boolean(fullLayer.disabled) || Boolean(fullLayer.inactive)}
                        label={
                          <Typography
                            sx={{
                              fontSize: fullLayer?.labelFontSize ?? fontSize,
                              lineHeight: 1.125,
                              fontWeight: fullLayer?.labelFontSize ? 600 : 400,
                              wordBreak: "break-word",
                              opacity: Boolean(fullLayer.disabled) || Boolean(fullLayer.inactive) ? 0.6 : 1
                            }}
                          >
                            {t(fullLayer?.name || item.name)}
                          </Typography>
                        }
                      />}
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {(fullLayer?.propertyFilter && !fullLayer?.disabled && !fullLayer?.inactive) && <LayerPropertyFilter layer={fullLayer} />}
                      {(fullLayer && !fullLayer?.disabled && !fullLayer?.inactive) && <LayerMenu layer={fullLayer} />}
                    </div>
                  </div>
                  {/* {fullLayer?.key === "rainsat" && props.renderRainsatVariantSwitch?.()} */}
                  {fullLayer?.key === "fluvial" && props.renderFluvialSelect?.()}
                  {fullLayer?.key === "pluvial" && props.renderPluvialSelect?.()}
                  {fullLayer?.key === "lake" && props.renderLakeSelect?.()}
                </Box>
              )}
            </HKVSortableList.Item>
          )
        }}
      />
    )
  }

  const renderInformationDialog = () => {
    if (!dialogConfig) return

    const { layer } = dialogConfig

    return (
      <HKVDialog
        config={dialogConfig}
        onClose={closeDialog}
      >
        <DialogTitle>{t("Information")} {t(layer.name)}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
          <Typography>{layer.layerControl.description}</Typography>
          <div className="dialog-actions">
            <Button
              variant="contained"
              className="dialog-button"
              onClick={closeDialog}
            >
              {t("Ok")}
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  return (
    <React.Fragment>
      {renderSortableGroup(layers)}
      {props.baseLayers !== false && renderSortableGroup(
        [
          {
            id: "BaseLayers",
            name: "baselayers",
            items: mapConfig.baseLayers.map((baseLayer) => ({
              id: baseLayer.key,
              name: baseLayer.name
            }))
          }
        ],
        null,
        0,
        "baselayers"
      )}
      {renderInformationDialog()}
    </React.Fragment>
  )
}

export default LayerControl